import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import Modal from './Modal';
import { useSelectStyles } from '../../utils/selectStyles';
import { buttonClass, formatCost, ghostButtonClass, inputClass } from '../../pages/picture-ai/usePictureAi';

const toExclusiveEnd = (date) => {
  if (!date) return null;
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() + 1);
  return value.toISOString().slice(0, 10);
};

/**
 * Re-run the configuration over pictures already in Gallery.
 *
 * The scope has to be computed before it can be launched: a replay costs money, and "12 000
 * pictures" is not a number anyone can act on without seeing where they are.
 */
const ReplayModal = ({ open, onClose, configuration, request }) => {
  const { t } = useTranslation();
  const selectStyles = useSelectStyles();

  const [form, setForm] = useState({ selection_mode: 'without_success', start_date: '', end_date: '', projectkeys: [], targeting_keys: [] });
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState(null);
  const [busy, setBusy] = useState(false);

  // Only the active version's combinations: a replay always runs the active version.
  const combinations = (configuration?.params?.targeting?.rules || []).map(rule => ({
    value: rule.key,
    label: (configuration.params.targeting.columns || ['item'])
      .map(column => rule.match?.[column] || t('any value'))
      .join(' · ')
  }));

  const update = (patch) => { setForm(current => ({ ...current, ...patch })); setPreview(null); setMessage(null); };

  const payload = () => ({
    ...form,
    projectkeys: form.projectkeys.map(option => option.value ?? option),
    targeting_keys: form.targeting_keys.map(option => option.value ?? option),
    end_date_exclusive: toExclusiveEnd(form.end_date),
    config_version_id: configuration?.active_version_id,
    request_key: crypto.randomUUID()
  });

  const call = async (path, onSuccess) => {
    setBusy(true);
    setMessage(null);
    try {
      onSuccess(await request(path, { method: 'POST', body: JSON.stringify(payload()) }));
    } catch (error) {
      setMessage({ tone: 'error', text: error.message });
    } finally {
      setBusy(false);
    }
  };

  const field = (label, control) => (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">{label}</span>
      {control}
    </label>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('Replay history')}
      subtitle={t('Re-run this configuration over pictures already in Gallery.')}
      footer={(
        <>
          <button className={ghostButtonClass} disabled={busy} onClick={() => call(`/admin/configurations/${configuration?.id}/replays/preview`, setPreview)}>
            {t('Compute the scope')}
          </button>
          <button
            className={buttonClass}
            disabled={busy || !preview || preview.count === 0}
            onClick={() => call(`/admin/configurations/${configuration?.id}/replays`, result => setMessage({
              tone: 'success',
              text: result.deduplicated
                ? t('This request was already enqueued ({{count}} picture(s)).', { count: result.enqueued })
                : t('{{count}} picture(s) enqueued.', { count: result.enqueued })
            }))}
          >
            {t('Launch the replay')}
          </button>
        </>
      )}
    >
      <div className="grid gap-4 lg:grid-cols-2">
        {field(t('Selection'), (
          <select className={inputClass} value={form.selection_mode} onChange={event => update({ selection_mode: event.target.value })}>
            <option value="all">{t('Every picture in scope')}</option>
            <option value="without_success">{t('Never analyzed successfully')}</option>
            <option value="last_failures">{t('Last attempt failed')}</option>
          </select>
        ))}
        {field(t('Transport'), (
          <div className={`${inputClass} flex items-center bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-300`}>
            {t('Batch only — half the price, and it never delays incoming pictures.')}
          </div>
        ))}
        {field(t('From'), <input type="date" className={inputClass} value={form.start_date} onChange={event => update({ start_date: event.target.value })} />)}
        {field(t('To'), <input type="date" className={inputClass} value={form.end_date} onChange={event => update({ end_date: event.target.value })} />)}
      </div>

      <div className="mt-4">
        <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">{t('Programs to replay (all by default)')}</span>
        <Select
          isMulti styles={selectStyles}
          options={(configuration?.programs || []).map(program => ({ value: program.projectkey, label: program.projectkey }))}
          value={form.projectkeys}
          onChange={options => update({ projectkeys: options || [] })}
        />
      </div>

      {combinations.length > 0 && (
        <div className="mt-4">
          <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">{t('Combinations to replay (all by default)')}</span>
          <Select
            isMulti styles={selectStyles}
            options={combinations}
            value={form.targeting_keys}
            onChange={options => update({ targeting_keys: options || [] })}
          />
        </div>
      )}

      {preview && (
        <div className="mt-4 rounded-xl bg-blue-50 p-4 text-sm dark:bg-blue-950/40">
          <p className="font-medium">
            {t('{{count}} picture(s) in scope', { count: preview.count })} · {t('estimated cost')} {formatCost(preview.estimated_cost_micros)}
          </p>
          <ul className="mt-1 text-xs">
            {preview.per_program.map(row => <li key={row.projectkey}>{row.projectkey} — {t('{{count}} picture(s)', { count: row.count })}</li>)}
          </ul>
        </div>
      )}

      {message && (
        <p className={`mt-4 rounded-lg p-3 text-sm ${message.tone === 'error'
          ? 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200'
          : 'bg-green-50 text-green-800 dark:bg-green-950/40 dark:text-green-200'}`}>
          {message.text}
        </p>
      )}
    </Modal>
  );
};

export default ReplayModal;
